/**
 * SectionNav — навигация разделов /bots (1–3 уровня).
 *
 * Приходит на смену DrillNav (3 неоморфные плиты друг под другом). Замечание
 * владельца 2026-07-27: уровни различались только вложенностью, поэтому
 * читались как один список из пятнадцати кнопок, а сам «вдавленный» хром
 * спорил с плоским остальным сайтом.
 *
 * ПРИНЦИП (разбор Kling, вариант 2): уровень опознаётся СИЛОЙ ОФОРМЛЕНИЯ,
 * а не позицией в стопке — три уровня получают три РАЗНЫЕ формы:
 *   L1 — залитая капсула, в одной строке с поиском (через разделитель);
 *   L2 — просто текст; активный плотнее + брендовая точка перед словом;
 *   L3 — мягкий чип.
 *
 * Почему у L2 точка, а не подчёркивание (решение владельца после сравнения
 * восьми вариантов в полном стеке):
 *   • подчёркивание растёт ВНИЗ, к чипам L3, и слипается с ними в грязную
 *     полосу — лечится только отступом, то есть возвратом высоты;
 *   • подложка-плашка делает L2 неотличимым от капсулы сверху и чипа снизу;
 *   • точка не занимает высоты вовсе и оставляет L2 текстом.
 * Свечение (text-shadow) добавлено ТОЛЬКО в тёмной теме — на светлом фоне
 * ореол читается как расфокус (проверено на макете).
 *
 * Совместимость с DrillNav сохранена намеренно: та же форма items, те же
 * виртуальные ключи (`__popular`, `__all`, `__all-tools`…). Ключи несут
 * URL-семантику — на них завязан `landingCtx` в пути карточки
 * `/bots/<раздел>/<ctx>/tool/<slug>`, переименование сломает ссылки.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { attachRowWheel } from '../../utils/rowWheel'

export interface SectionNavItem {
	key: string
	label: string
	/** Счётчик справа от подписи (лента аудио: сколько треков в фильтре). */
	count?: number
	/** Плашка «BETA» рядом с подписью (Озвучка/TTS). */
	betaBadge?: boolean
	/** Задан → пилюля рендерится как NavLink (Cmd+click, history).
	 *  Не задан → button с onSelect (Files и фильтры аудио — состояние). */
	to?: string
	/** Для NavLink: точное совпадение пути. */
	end?: boolean
	/** Дети активного L2 становятся элементами L3. */
	children?: SectionNavItem[]
	/** Пункт означает «без фильтра» («Все», «Популярные») — в путь свёрнутой
	 *  строки не идёт. Ключи с `__` считаются виртуальными и без флага: у
	 *  разделов эти ключи несут URL-семантику и переименовать их нельзя. */
	virtual?: boolean
}

export interface SectionNavProps {
	l1Items: SectionNavItem[]
	selectedL1: string
	onSelectL1: (key: string) => void
	l1AriaLabel?: string

	l2Items?: SectionNavItem[]
	selectedL2?: string | null
	onSelectL2?: (key: string) => void
	l2AriaLabel?: string
	/** Показывать ли L2 (условие знает вызывающая страница). */
	showL2?: boolean

	selectedL3?: string | null
	onSelectL3?: (key: string) => void
	l3AriaLabel?: string

	/** Слот поиска — встаёт в строку L1 за разделителем. */
	search?: ReactNode
	/** Слот действий раздела (кнопки режима, переключатель вида) — правый
	 *  край строки L1. В свёрнутом виде остаётся: это управление тем, что
	 *  юзер сейчас видит, а не навигация. */
	actions?: ReactNode
	/** Отдельный ряд под строкой разделов — для действий над выбранным
	 *  (/files: «Выбрать все», «Удалить»). В отличие от рядов навигации НЕ
	 *  сворачивается при прокрутке: пока юзер что-то выделил, кнопки должны
	 *  оставаться под рукой. */
	actionsRow?: ReactNode
	/** Открыт ли ряд действий. Разметку держим смонтированной и в закрытом
	 *  состоянии — иначе ряд появлялся бы рывком, без анимации высоты. */
	actionsRowOpen?: boolean
	className?: string
	/** Доп. класс внешней обёртки (липкость, ширина, стекло живут на ней). */
	wrapClassName?: string
}

export default function SectionNav({
	l1Items,
	selectedL1,
	onSelectL1,
	l1AriaLabel = 'Раздел',
	l2Items,
	selectedL2,
	onSelectL2,
	l2AriaLabel = 'Категория',
	showL2 = !!l2Items,
	selectedL3,
	onSelectL3,
	l3AriaLabel = 'Подкатегория',
	search,
	actions,
	actionsRow,
	actionsRowOpen = false,
	className,
	wrapClassName,
}: SectionNavProps) {
	const activeL2 = selectedL2 && l2Items ? l2Items.find((it) => it.key === selectedL2) : null
	const l3Items = activeL2?.children ?? []
	const hasL3 = showL2 && l3Items.length > 0

	// ── Свёрнутое состояние (скролл) ───────────────────────────────
	// Ряды уезжают, остаётся одна строка контекста «Раздел · Категория».
	//
	// Порог ловит SENTINEL над липкой обёрткой — узел в обычном потоке. Он не
	// зависит от того, свёрнута панель или нет, поэтому обратной связи
	// «сменили высоту → сместился порог → сменили обратно» не возникает:
	// именно такая петля давала «тряску контента» и заставила снять прежний
	// scroll-collapse в старой навигации.
	//
	// По `offsetTop` обёртки считать нельзя: у sticky-элемента он плывёт
	// вместе со скроллом, и условие балансирует на границе (проверено живьём).
	const rootRef = useRef<HTMLDivElement>(null)
	const wrapRef = useRef<HTMLDivElement>(null)
	// Эталонная высота развёрнутой панели — делится между скролл-эффектом
	// (замер) и layout-эффектом состояния (синхронная компенсация).
	const expandedHRef = useRef(0)
	const [collapsed, setCollapsed] = useState(false)
	// Юзер раскрыл свёрнутую строку — держим ряды, пока не свернёт сам или
	// не уедет обратно вверх (иначе следующий кадр скролла схлопнул бы).
	const [pinned, setPinned] = useState(false)

	useEffect(() => {
		const wrap = wrapRef.current
		const host = wrap?.parentElement
		if (!wrap || !host) return

		// Sentinel вставляем ПЕРЕД липкой обёрткой и в обычный поток: внутри
		// самой обёртки он уезжал бы вместе с ней и порог никогда не наступал.
		// Императивно, а не через JSX, чтобы страницам не приходилось помнить
		// про служебный узел — компонент самодостаточен.
		const sentinel = document.createElement('div')
		sentinel.className = 'snav-sentinel'
		sentinel.setAttribute('aria-hidden', 'true')
		host.insertBefore(sentinel, wrap)

		// ── Спейсер-компенсатор высоты ─────────────────────────────
		// Сжатие панели анимирует её высоту В ПОТОКЕ: каждый кадр 320мс-перехода
		// браузер перекладывал всю витрину под ней (130+ плиток — до 55мс/кадр,
		// замерено 2026-08-12), а контент уезжал на ~60px — «дёргание» и жалобы
		// на тормоза после сжатия. Спейсер стоит СРАЗУ ЗА обёрткой и каждый кадр
		// добирает ровно столько, сколько панель отдала: сумма высот постоянна,
		// документ не шевелится, перекладка ниже панели не происходит.
		//
		// ResizeObserver, а не согласованные CSS-транзишены: панель складывается
		// из двух анимаций с разными таймингами (ряды 320мс + паддинги 300мс),
		// синхронный «обратный» транзишен у спейсера дал бы остаточный дрейф.
		// RO срабатывает до отрисовки кадра — компенсация точная по факту.
		const spacer = document.createElement('div')
		spacer.className = 'snav-spacer'
		spacer.setAttribute('aria-hidden', 'true')
		host.insertBefore(spacer, wrap.nextSibling)

		// Эталон — высота развёрнутой панели в покое; обновляется, когда панель
		// развёрнута и переход завершён (смена категории меняет состав рядов).
		expandedHRef.current = wrap.offsetHeight
		let collapsedNow = false
		let lastFlipAt = 0
		let retryTimer = 0
		// Окно перехода «свёрнута → развёрнута»: состояние уже expanded, но ряды
		// ещё растут — без окна спейсер схлопнулся бы мгновенно (скачок на 60px).
		let settling = 0
		const sync = () => {
			const h = wrap.offsetHeight
			if (collapsedNow || settling) {
				spacer.style.height = `${Math.max(0, expandedHRef.current - h)}px`
			} else {
				expandedHRef.current = h
				spacer.style.height = '0px'
			}
		}
		// На время перехода компенсацию гоняет собственный rAF-цикл: RO
		// доставляет колбэк на кадр позже анимации (замерено: сумма гуляла
		// ±12px), а чтение offsetHeight + запись высоты в одном кадре до
		// отрисовки держат сумму точной. Цикл живёт только ~400мс перехода.
		let syncFrame = 0
		const syncLoop = () => {
			sync()
			syncFrame = settling ? window.requestAnimationFrame(syncLoop) : 0
		}
		const markFlip = () => {
			window.clearTimeout(settling)
			settling = window.setTimeout(() => {
				settling = 0
				sync()
			}, 380)
			sync()
			if (!syncFrame) syncFrame = window.requestAnimationFrame(syncLoop)
		}
		// RO остаётся дежурным: ловит смену состава рядов в покое (категория с
		// подкатегориями/без) и ресайзы — обновляет эталонную высоту.
		const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null
		ro?.observe(wrap)

		// Порог читаем НА КАЖДОМ кадре, а не один раз при монтировании:
		// липкий `top` у нас плавающий — он разный на мобиле и десктопе и
		// меняется, когда уезжает шапка. Зафиксированный порог (через
		// rootMargin у IntersectionObserver) после такой смены переставал
		// совпадать с реальным положением, и группа не сворачивалась.
		let frame = 0
		let lastY = window.scrollY
		// Зазор между датчиком и панелью в ПОКОЕ. На /bots он нулевой (панель —
		// первый элемент), но на /files её отделяет отступ шапки: считая порог
		// от нуля, компонент открывал страницу уже свёрнутой. Замеряем, пока
		// страница наверху, и пересчитываем при каждом возврате наверх.
		let baseGap = 0
		const read = () => {
			frame = 0
			// «Прилипла» = обёртка ОТОРВАЛАСЬ от своего места в потоке: датчик
			// стоит там, где она лежала бы без липкости, и уезжает вверх со
			// страницей, а сама обёртка остаётся у верхней кромки.
			//
			// Сравнивать положение датчика с величиной `top` НЕЛЬЗЯ: когда
			// навигация опустилась под шапку (top 84), датчик оказался выше
			// этого значения ещё при нулевой прокрутке — условие срабатывало
			// сразу, и навигация зависала свёрнутой (сломал этим 2026-07-28).
			const gap = wrap.getBoundingClientRect().top - sentinel.getBoundingClientRect().top
			if (window.scrollY <= 1) baseGap = gap
			const gone = gap - baseGap > 4
			// Выдержка 280мс между переключениями: шаговое колесо (Windows,
			// ~120px за щелчок) дребезжит на границе порога — без выдержки
			// каждый щелчок дёргал панель туда-обратно (замер: 8 смен за
			// проход). Первое переключение проходит сразу.
			if (gone !== collapsedNow) {
				const now = performance.now()
				if (now - lastFlipAt > 280) {
					collapsedNow = gone
					lastFlipAt = now
					markFlip()
					setCollapsed(gone)
					if (!gone) setPinned(false)
				} else {
					// Отклонённое переключение обязано повториться: если юзер
					// остановил скролл сразу после него, событий больше не будет
					// и панель залипла бы в неверном состоянии.
					window.clearTimeout(retryTimer)
					retryTimer = window.setTimeout(() => {
						if (!frame) frame = window.requestAnimationFrame(read)
					}, 300 - (now - lastFlipAt))
				}
			}
			// Раскрыли ряды поверх контента и поехали дальше вниз — сворачиваем
			// обратно: юзер вернулся к просмотру витрины, держать шапку
			// раскрытой незачем (просьба владельца). Порог 40px, чтобы дрожание
			// колеса не схлопывало сразу после клика.
			const y = window.scrollY
			if (y - lastY > 40) setPinned(false)
			lastY = y
		}
		const onScroll = () => {
			if (!frame) frame = window.requestAnimationFrame(read)
		}
		read()
		window.addEventListener('scroll', onScroll, { passive: true })
		window.addEventListener('resize', onScroll)
		return () => {
			if (frame) window.cancelAnimationFrame(frame)
			if (syncFrame) window.cancelAnimationFrame(syncFrame)
			window.clearTimeout(retryTimer)
			window.clearTimeout(settling)
			ro?.disconnect()
			window.removeEventListener('scroll', onScroll)
			window.removeEventListener('resize', onScroll)
			sentinel.remove()
			spacer.remove()
		}
	}, [])

	// ── Подъём к краю, когда шапка ушла ────────────────────────────
	// Шапка сайта sticky и держится только первые ~460px прокрутки, дальше
	// уезжает. Пока она на экране, навигация стоит под ней (см. `top` в
	// _section-nav.scss); как только ушла — забирает освободившееся место
	// (просьба владельца), иначе сверху остаётся пустая полоса.
	//
	// Обратной связи нет: положение шапки от нашего `top` не зависит, поэтому
	// переключение не может само себя переоткрыть.
	useEffect(() => {
		const wrap = wrapRef.current
		const header = document.querySelector('.header')
		if (!wrap || !header || typeof IntersectionObserver === 'undefined') return
		const io = new IntersectionObserver(
			([entry]) => {
				wrap.dataset.headerGone = entry.isIntersecting ? 'false' : 'true'
			},
			{ threshold: 0 },
		)
		io.observe(header)
		return () => {
			io.disconnect()
			delete wrap.dataset.headerGone
		}
	}, [])

	const expanded = !collapsed || pinned

	// ── Автоскролл активного элемента в видимую зону ───────────────
	// Ряды скроллятся горизонтально; после перехода по ссылке активный
	// может быть за краем. useLayoutEffect — до paint, иначе виден рывок
	// «начало → активный».
	useLayoutEffect(() => {
		const root = rootRef.current
		if (!root) return
		root.querySelectorAll<HTMLElement>('[data-snav-rail]').forEach((rail) => {
			const active = rail.querySelector<HTMLElement>('.is-active')
			if (!active) return
			const target = active.offsetLeft - (rail.clientWidth - active.offsetWidth) / 2
			rail.scrollLeft = Math.max(0, target)
		})
	}, [selectedL1, selectedL2, selectedL3, expanded])

	const activeL1Label = l1Items.find((it) => it.key === selectedL1)?.label ?? ''
	// Полный путь: раздел · категория · подкатегория. Виртуальные пункты
	// («Популярные», «Все» — ключи с `__` либо флаг `virtual`) в путь не идут:
	// они означают «без фильтра», а не выбранную ветку.
	const activeL3 = selectedL3 ? l3Items.find((it) => it.key === selectedL3) : null
	const isVirtual = (it: SectionNavItem | null | undefined) => !it || it.virtual || it.key.startsWith('__')
	const contextLabel = [
		activeL1Label,
		isVirtual(activeL2) ? null : activeL2?.label,
		isVirtual(activeL3) ? null : activeL3?.label,
	]
		.filter(Boolean)
		.join(' · ')

	const togglePinned = useCallback(() => setPinned((v) => !v), [])

	// Состояние пишем НА ОБЁРТКУ: стекло, размеры и липкость живут там
	// (внутри backdrop root блюр не работает — подробности в _section-nav.scss).
	//
	// Ширину свёрнутой группы НЕ считаем в JS: измерение кнопки упиралось в то,
	// что мы этим же значением её и сжимаем — получался замкнутый круг, подпись
	// превращалась в огрызок. Ширину отдаём браузеру (`fit-content` в стилях),
	// он считает её по натуральному содержимому и не ошибается.
	useLayoutEffect(() => {
		const wrap = wrapRef.current
		if (!wrap) return
		wrap.dataset.snav = expanded ? 'expanded' : 'compact'
		wrap.dataset.snavStuck = collapsed ? 'true' : 'false'
		// Компенсация СИНХРОННО со сменой состояния: чтение offsetHeight
		// применяет новый CSS, спейсер добирает разницу — обе правки в одном
		// кадре отрисовки. RO/rAF-страховка в скролл-эффекте опаздывала на
		// кадр (одно-кадровый проскок ~36-60px на шаговом колесе Windows).
		const spacer = wrap.nextElementSibling
		if (spacer instanceof HTMLElement && spacer.classList.contains('snav-spacer')) {
			const h = wrap.offsetHeight
			if (expanded && !collapsed) {
				expandedHRef.current = h
				spacer.style.height = '0px'
			} else {
				spacer.style.height = `${Math.max(0, expandedHRef.current - h)}px`
			}
		}
	}, [expanded, collapsed])

	return (
		// Обёртку рендерит САМ компонент: на ней живут липкость, ширина и
		// матовый фон, и она должна быть у каждой страницы одинаковой.
		// Раньше её ставила страница — легко забыть или обернуть лишним слоем,
		// а любой слой между ней и `.snav` ломает и стекло, и порог сворачивания.
		<div ref={wrapRef} className={['bots-section-tabs', wrapClassName].filter(Boolean).join(' ')}>
			<div
				ref={rootRef}
				className={['snav', className].filter(Boolean).join(' ')}
				data-l1={selectedL1}
				data-l2={selectedL2 ?? ''}
			>
				{/* Строка 1. Капсулы разделов и строка контекста лежат друг на
				    друге и меняются кроссфейдом; поиск общий для обоих состояний,
				    поэтому не перемонтируется и не теряет введённый запрос. */}
				<div className="snav__top">
					{/* Кнопка есть в ОБОИХ состояниях прилипшей панели и на одном
					    месте — меняется только направление шеврона. Шеврон тот же,
					    что у кнопки сворачивания виджета (.gw__close-btn): одно
					    действие — один знак.
					    Живёт ОТДЕЛЬНО от слоя подмены: внутри него она пропадала
					    вместе с ним, когда на мобиле раскрывается поиск и слой
					    скрывается целиком (замечание владельца). */}
					{collapsed && (
						<button
							type="button"
							className="snav__collapse"
							onClick={togglePinned}
							aria-label={expanded ? 'Свернуть навигацию' : 'Развернуть навигацию'}
						>
							<svg width="15" height="15" viewBox="0 0 13 13" fill="none" aria-hidden="true">
								<path
									d={expanded ? 'M2 8.5L6.5 4L11 8.5' : 'M2 4.5L6.5 9L11 4.5'}
									stroke="currentColor"
									strokeWidth="1.8"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
					)}

					<div className="snav__swap">
						<Rail className="snav__caps" ariaLabel={l1AriaLabel} shown={expanded}>
							{l1Items.map((tab, idx) => (
								<Pill
									key={tab.key}
									item={tab}
									idx={idx}
									level={1}
									isActive={tab.key === selectedL1}
									onSelect={onSelectL1}
									enabled={expanded}
								/>
							))}
						</Rail>

						<button
							type="button"
							className="snav__context"
							data-shown={expanded ? 'false' : 'true'}
							onClick={togglePinned}
							aria-hidden={expanded}
							tabIndex={expanded ? -1 : 0}
						>
							{/* key — чтобы React пересоздал узел при смене пути: так
							    отрабатывает анимация появления (приём из design_language). */}
							<span key={contextLabel} className="snav__context-label">
								{contextLabel || activeL1Label}
							</span>
							<span className="snav__context-caret" aria-hidden="true">▾</span>
						</button>
					</div>

					{search && (
						<div className="snav__search">{search}</div>
					)}

					{actions && <div className="snav__actions">{actions}</div>}

					{/* Слот кнопки «Инструкция»: она живёт в каталоге (там же состояние
					    обучалки) и телепортируется сюда порталом, если раздел её
					    показывает. Пустой узел ничего не занимает — на разделах без
					    обучалки строка выглядит как раньше. Стоит ПОСЛЕ поиска
					    (решение владельца): лупа ближе к рядам, справка — с краю. */}
					<span className="snav__sep" data-shown={expanded ? 'true' : 'false'} aria-hidden="true" />
					<div className="snav__help" data-snav-help-slot />
				</div>

				{/* Ряд действий над выбранным. Открыт независимо от того, свёрнута
				    панель или нет: это работа с текущим выделением, а не навигация.
				    Содержимое прижато вправо (решение владельца) — иначе ряд
				    читается как ещё один уровень меню. */}
				{actionsRow && (
					<div className="snav__row" data-open={actionsRowOpen ? 'true' : 'false'}>
						<div className="snav__row-clip">
							<div className="snav__action-row">{actionsRow}</div>
						</div>
					</div>
				)}

				{/* L2 — текст; активный с точкой (см. шапку файла). */}
				{l2Items && onSelectL2 && (
					<div className="snav__row" data-open={expanded && showL2 ? 'true' : 'false'}>
						<Rail className="snav__cats" ariaLabel={l2AriaLabel} clip>
							{l2Items.map((cat, idx) => (
								<Pill
									key={cat.key}
									item={cat}
									idx={idx}
									level={2}
									isActive={cat.key === selectedL2}
									onSelect={onSelectL2}
									enabled={expanded && showL2}
								/>
							))}
						</Rail>
					</div>
				)}

				{/* L3 — чипы. Раскрываются, только когда у активной категории есть
				    дети: на входе в раздел пустого ряда нет. */}
				{onSelectL3 && (
					<div className="snav__row" data-open={expanded && hasL3 ? 'true' : 'false'}>
						<Rail className="snav__subs" ariaLabel={l3AriaLabel} clip>
							{l3Items.map((sub, idx) => (
								<Pill
									key={sub.key}
									item={sub}
									idx={idx}
									level={3}
									isActive={sub.key === selectedL3}
									onSelect={onSelectL3}
									enabled={expanded && hasL3}
								/>
							))}
						</Rail>
					</div>
				)}
			</div>
		</div>
	)
}

// ─── Rail ──────────────────────────────────────────────────────────
// Горизонтальный ряд, который умеет прокручиваться МЫШЬЮ.
//
// Ряды переполняются (категорий бывает под десяток), а полосу прокрутки мы
// прячем по языку интерфейса. На трекпаде и на телефоне это незаметно — там
// есть горизонтальный жест, — но обычной мышью прокрутить было нечем
// (замечание владельца). Даём два способа сразу:
//   • колесо — вертикальное прокручивание переводим в горизонтальное;
//   • стрелки по краям — появляются только при переполнении и только с той
//     стороны, куда ещё есть куда ехать.
// Fade-маску у краёв намеренно НЕ добавляем: её уже забраковали на прежней
// навигации — крайние элементы не должны обрезаться градиентом.
function Rail({
	className,
	ariaLabel,
	children,
	shown,
	clip,
}: {
	className: string
	ariaLabel: string
	children: ReactNode
	/** Строка L1 меняется со строкой контекста кроссфейдом: скрытая половина
	 *  уходит в абсолют, поэтому состояние держит ОБЁРТКА рейла, а не сам ряд —
	 *  иначе стрелки остаются висеть над свёрнутой панелью. */
	shown?: boolean
	/** Ряды 2 и 3 схлопываются по высоте, поэтому им нужен обрезающий слой.
	 *  Он оборачивает ТОЛЬКО прокручиваемый ряд: когда слой лежал снаружи,
	 *  он срезал вынесенные за край кнопки пополам (замечание владельца). */
	clip?: boolean
}) {
	const ref = useRef<HTMLDivElement>(null)
	const [edges, setEdges] = useState({ left: false, right: false })

	const sync = useCallback(() => {
		const el = ref.current
		if (!el) return
		const max = el.scrollWidth - el.clientWidth
		setEdges({ left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 })
	}, [])

	useLayoutEffect(() => {
		const el = ref.current
		if (!el) return
		sync()
		el.addEventListener('scroll', sync, { passive: true })
		// Содержимое меняется (другая категория → другие подкатегории) и
		// ширина тоже (ресайз, схлопывание группы) — пересчитываем стрелки.
		const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null
		ro?.observe(el)
		return () => {
			el.removeEventListener('scroll', sync)
			ro?.disconnect()
		}
	}, [sync, children])

	// Колесо — общее правило utils/rowWheel (то же у полок DragScrollRow):
	// щелчок мыши листает рельсу, пока ехать есть куда, жест трекпада уходит
	// странице — иначе она «подтормаживала» под курсором над рельсой.
	useEffect(() => {
		const el = ref.current
		if (!el) return
		return attachRowWheel(el)
	}, [])

	const nudge = (dir: -1 | 1) => {
		const el = ref.current
		if (!el) return
		el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.8), behavior: 'smooth' })
	}

	const arrows = (
		<>
			<button
				type="button"
				className="snav__arrow snav__arrow--prev"
				data-shown={edges.left ? 'true' : 'false'}
				onClick={() => nudge(-1)}
				aria-label="Прокрутить назад"
				tabIndex={-1}
			>
				<Chevron dir="prev" />
			</button>
			<button
				type="button"
				className="snav__arrow snav__arrow--next"
				data-shown={edges.right ? 'true' : 'false'}
				onClick={() => nudge(1)}
				aria-label="Прокрутить вперёд"
				tabIndex={-1}
			>
				<Chevron dir="next" />
			</button>
		</>
	)

	const row = (
		<div ref={ref} className={className} data-snav-rail role="tablist" aria-label={ariaLabel}>
			{children}
		</div>
	)

	// Схлопывающийся ряд: обрезающий слой обязан быть ПРЯМЫМ ребёнком строки —
	// `grid-template-rows: 0fr` сжимает её только через элемент, который сам
	// режет содержимое. Кнопки при этом его СОСЕДИ, а не потомки: внутри слоя
	// их срезало пополам, стоило вынести за край зоны (замечание владельца).
	if (clip) {
		return (
			<>
				<div className="snav__row-clip">{row}</div>
				{arrows}
			</>
		)
	}

	return (
		<div className="snav__rail" data-shown={shown === undefined ? undefined : shown ? 'true' : 'false'}>
			{arrows}
			{row}
		</div>
	)
}

// ─── Chevron ───────────────────────────────────────────────────────
// Один знак на всю навигацию: тот же угол, что у кнопки сворачивания панели
// и у виджета. Раньше стрелки рисовались символами «‹ ›» — они зависят от
// шрифта, не совпадают по толщине с остальной графикой и стоят не по центру.
function Chevron({ dir }: { dir: 'prev' | 'next' }) {
	return (
		<svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
			<path
				d={dir === 'prev' ? 'M8.5 2L4 6.5L8.5 11' : 'M4.5 2L9 6.5L4.5 11'}
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

// ─── Pill ──────────────────────────────────────────────────────────
// NavLink при `to` (Cmd+click, история браузера), иначе button. Активность
// приходит пропом, а не из NavLink: URL бывает сложной формы (виртуальные
// пеблы, лендинг карточки поверх витрины) — знает об этом только страница.
interface PillProps {
	item: SectionNavItem
	idx: number
	level: 1 | 2 | 3
	isActive: boolean
	onSelect: (key: string) => void
	enabled: boolean
}

function Pill({ item, idx, level, isActive, onSelect, enabled }: PillProps) {
	const className = `snav__item snav__item--l${level}${isActive ? ' is-active' : ''}`
	const style = { '--item-idx': idx } as CSSProperties
	const inner = (
		<>
			{/* Точка активного L2 — отдельным узлом, чтобы не тянуть ::before
			    на элемент со скроллом (у псевдоэлемента нет своей ширины в
			    расчёте offsetLeft автоскролла). */}
			{level === 2 && isActive && <span className="snav__dot" aria-hidden="true" />}
			<span className="snav__label">{item.label}</span>
			{item.count !== undefined && <span className="snav__count">{item.count}</span>}
			{item.betaBadge && <span className="beta-badge">BETA</span>}
		</>
	)

	if (item.to) {
		return (
			<NavLink
				to={item.to}
				end={item.end}
				role="tab"
				aria-pressed={isActive}
				className={className}
				style={style}
				tabIndex={enabled ? 0 : -1}
				onClick={() => onSelect(item.key)}
			>
				{inner}
			</NavLink>
		)
	}

	return (
		<button
			type="button"
			role="tab"
			aria-pressed={isActive}
			className={className}
			style={style}
			tabIndex={enabled ? 0 : -1}
			onClick={() => onSelect(item.key)}
		>
			{inner}
		</button>
	)
}
